<template>
  <div>
    <add-new-school :is-add-new-school-sidebar-active.sync="isAddNewSchoolSidebarActive" :prefill="prefill"
      @sendschooldata="createSchoolFromRequest" />

    <!-- Request details -->
    <b-modal v-model="showDetails" size="lg" scrollable centered
      :title="selected ? selected.schoolName : 'Sign-up request'" @hidden="closeDetails">
      <b-overlay :show="detailsLoading" rounded="sm">
        <div v-if="selected">
          <div class="d-flex align-items-center mb-2">
            <b-avatar v-if="selected.schoolImg" :src="selected.schoolImg" size="64" rounded class="mr-1" />
            <b-avatar v-else variant="light-primary" size="64" rounded class="mr-1" :text="initials(selected.schoolName)" />
            <div>
              <h4 class="mb-25">{{ selected.schoolName }}</h4>
              <b-badge pill :variant="'light-' + statusVariant(selected.status)" class="text-capitalize">
                {{ selected.status || "pending" }}
              </b-badge>
              <small class="text-muted ml-50">Requested {{ formatDate(selected.createdAt) }}</small>
            </div>
          </div>

          <b-alert v-if="selected.status === 'rejected'" show variant="danger" class="p-1">
            <small>Rejected {{ formatDate(selected.reviewedAt) }}<template v-if="selected.rejectReason">:
                {{ selected.rejectReason }}</template></small>
          </b-alert>

          <h6 class="text-uppercase text-muted font-small-2 mb-1">School</h6>
          <b-row class="mb-1">
            <b-col v-for="item in schoolDetails" :key="item.label" cols="12" md="6" class="mb-1">
              <small class="text-muted d-block">{{ item.label }}</small>
              <span class="font-weight-bold">{{ item.value }}</span>
            </b-col>
          </b-row>

          <h6 class="text-uppercase text-muted font-small-2 mb-1">Contact person (becomes the school admin)</h6>
          <b-row class="mb-1">
            <b-col cols="12" md="4" class="mb-1">
              <small class="text-muted d-block">Name</small>
              <span class="font-weight-bold">{{ selected.contactName || "-" }}</span>
            </b-col>
            <b-col cols="12" md="4" class="mb-1">
              <small class="text-muted d-block">Phone</small>
              <b-link v-if="selected.contactPhone" :href="'tel:' + selected.contactPhone">{{ selected.contactPhone }}</b-link>
              <span v-else>-</span>
            </b-col>
            <b-col cols="12" md="4" class="mb-1">
              <small class="text-muted d-block">Email (portal login)</small>
              <b-link v-if="selected.contactEmail" :href="'mailto:' + selected.contactEmail" class="text-break">
                {{ selected.contactEmail }}
              </b-link>
              <span v-else>-</span>
            </b-col>
          </b-row>

          <template v-if="selected.note">
            <h6 class="text-uppercase text-muted font-small-2 mb-50">Note</h6>
            <p class="mb-2 request-note-full">{{ selected.note }}</p>
          </template>

          <h6 class="text-uppercase text-muted font-small-2 mb-1">Verification documents</h6>
          <b-list-group>
            <b-list-group-item v-for="doc in selected.documents || []" :key="documentId(doc)"
              class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <feather-icon :icon="doc.type === 'application/pdf' ? 'FileTextIcon' : 'ImageIcon'" size="18"
                  class="mr-1 text-primary" />
                <div>
                  <div class="font-weight-bold text-break">{{ doc.name }}</div>
                  <small class="text-muted">{{ fileType(doc.type) }} · {{ formatSize(doc.size) }}</small>
                </div>
              </div>
              <b-button size="sm" variant="outline-primary" class="text-nowrap"
                :disabled="openingDocument === documentId(doc)" @click="openDocument(doc)">
                <b-spinner v-if="openingDocument === documentId(doc)" small class="mr-25" />
                <feather-icon v-else icon="ExternalLinkIcon" size="14" class="mr-25" />Open
              </b-button>
            </b-list-group-item>
            <b-list-group-item v-if="!(selected.documents || []).length" class="text-muted">
              No documents
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-overlay>

      <template #modal-footer>
        <div v-if="selected && isPending(selected)" class="d-flex">
          <b-button variant="outline-danger" class="mr-1" @click="rejectRequest(selected)">
            <feather-icon icon="XIcon" size="14" class="mr-25" />Reject
          </b-button>
          <b-button variant="success" :disabled="detailsLoading" @click="approveRequest(selected)">
            <feather-icon icon="CheckIcon" size="14" class="mr-25" />Approve
          </b-button>
        </div>
        <b-link v-else-if="selected && selected.status === 'approved' && selected.schoolId"
          :to="{ path: '/school-management/' + selected.schoolId }"
          @click="sendPropsToSchoolGrade(selected.schoolName, selected.schoolId)">
          View School
          <feather-icon icon="ArrowRightIcon" size="14" />
        </b-link>
        <b-button v-else variant="outline-secondary" @click="showDetails = false">Close</b-button>
      </template>
    </b-modal>

    <!-- Status filters -->
    <b-row class="match-height">
      <b-col v-for="tab in statusTabs" :key="tab.value" cols="6" lg="3">
        <b-card no-body class="status-filter-card cursor-pointer" :class="{ 'status-filter-card--active': status === tab.value }"
          :style="status === tab.value ? { borderColor: tab.color } : {}" @click="changeStatus(tab.value)">
          <b-card-body class="d-flex align-items-center justify-content-between">
            <div>
              <h3 class="font-weight-bolder mb-0">{{ counts[tab.countKey] || 0 }}</h3>
              <span class="text-muted">{{ tab.label }}</span>
            </div>
            <b-avatar :variant="'light-' + tab.variant" size="42">
              <feather-icon :icon="tab.icon" size="20" />
            </b-avatar>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-card no-body>
      <div class="m-2">
        <b-row class="align-items-center">
          <b-col cols="12" md="6" class="mb-1 mb-md-0">
            <h4 class="mb-25">{{ currentTab.label }} Sign-ups</h4>
            <small class="text-muted">Schools that signed up on the school portal. Check the documents before approving.</small>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-input-group class="input-group-merge mr-1">
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" />
                </b-input-group-prepend>
                <b-form-input v-model="searchQuery" placeholder="Search school, contact, email or city..." />
              </b-input-group>
              <b-button variant="outline-primary" v-b-tooltip.hover title="Refresh" @click="getRequests">
                <feather-icon icon="RefreshCwIcon" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-overlay :show="loading" rounded="sm">
        <b-table-simple hover responsive class="mb-0 school-requests-table">
          <b-thead head-variant="light">
            <b-tr>
              <b-th>School</b-th>
              <b-th>Contact</b-th>
              <b-th class="text-center">Students</b-th>
              <b-th class="text-center">Staff / Users</b-th>
              <b-th class="text-center">Documents</b-th>
              <b-th>Requested</b-th>
              <b-th>Status</b-th>
              <b-th class="text-right">Actions</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="request in filteredRequests" :key="request._id" class="cursor-pointer"
              @click="viewRequest(request)">
              <b-td>
                <div class="d-flex align-items-center">
                  <b-avatar variant="light-primary" size="36" class="mr-1" :text="initials(request.schoolName)" />
                  <div>
                    <div class="font-weight-bold text-body">{{ request.schoolName || "-" }}</div>
                    <small class="text-muted d-flex align-items-center">
                      <template v-if="request.city">
                        <feather-icon icon="MapPinIcon" size="12" class="mr-25" />{{ request.city }}
                      </template>
                      <template v-else>No city</template>
                    </small>
                  </div>
                </div>
              </b-td>
              <b-td>
                <div class="font-weight-bold text-nowrap">{{ request.contactName || "-" }}</div>
                <small v-if="request.contactEmail" class="d-block text-muted">{{ request.contactEmail }}</small>
                <small v-if="request.contactPhone" class="d-block text-muted text-nowrap">{{ request.contactPhone }}</small>
              </b-td>
              <b-td class="text-center">
                <span class="font-weight-bold">{{ formatNumber(request.totalStudents) }}</span>
              </b-td>
              <b-td class="text-center">
                <span class="font-weight-bold">{{ formatNumber(request.staffCount) }}</span>
              </b-td>
              <b-td class="text-center">
                <b-badge variant="light-primary" pill>
                  <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />{{ (request.documents || []).length }}
                </b-badge>
              </b-td>
              <b-td>
                <div class="text-nowrap">{{ formatDate(request.createdAt) }}</div>
              </b-td>
              <b-td>
                <b-badge pill :variant="'light-' + statusVariant(request.status)" class="text-capitalize">
                  {{ request.status || "pending" }}
                </b-badge>
                <small v-if="request.reviewedAt" class="d-block text-muted text-nowrap mt-25">
                  {{ formatDate(request.reviewedAt) }}
                </small>
              </b-td>
              <b-td class="text-right">
                <b-button size="sm" variant="outline-primary" class="text-nowrap" @click.stop="viewRequest(request)">
                  <feather-icon icon="EyeIcon" size="14" class="mr-25" />View
                </b-button>
              </b-td>
            </b-tr>

            <b-tr v-if="!loading && !filteredRequests.length">
              <b-td colspan="8">
                <div class="text-center py-5">
                  <b-avatar variant="light-secondary" size="56" class="mb-1">
                    <feather-icon icon="InboxIcon" size="26" />
                  </b-avatar>
                  <h5 class="mb-25">No {{ status ? currentTab.label.toLowerCase() : "" }} sign-ups</h5>
                  <small class="text-muted">
                    {{ searchQuery ? "Nothing matches your search." : "Schools that sign up on the school portal will show up here." }}
                  </small>
                </div>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BButton,
  BLink,
  BBadge,
  BAvatar,
  BAlert,
  BOverlay,
  BModal,
  BSpinner,
  BListGroup,
  BListGroupItem,
  BTableSimple,
  BThead,
  BTr,
  BTh,
  BTd,
  BTbody,
  VBTooltip,
} from "bootstrap-vue";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/src/sweetalert2.scss";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddNewSchool from "./app/list/users-list/AddNewSchool";

const STATUS_TABS = [
  { value: "pending", countKey: "pending", label: "Pending", variant: "warning", color: "#ff9f43", icon: "ClockIcon" },
  { value: "approved", countKey: "approved", label: "Approved", variant: "success", color: "#28c76f", icon: "CheckCircleIcon" },
  { value: "rejected", countKey: "rejected", label: "Rejected", variant: "danger", color: "#ea5455", icon: "XCircleIcon" },
  { value: "", countKey: "all", label: "All", variant: "primary", color: "#7367f0", icon: "InboxIcon" },
];

const FILE_TYPES = { "application/pdf": "PDF", "image/png": "PNG", "image/jpeg": "JPG" };

// School portal sign-ups (school_requests with source "portal"). Approving creates the school with
// the CMS "Add School" form and makes the contact email the school admin.
export default {
  components: {
    AddNewSchool,
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BButton,
    BLink,
    BBadge,
    BAvatar,
    BAlert,
    BOverlay,
    BModal,
    BSpinner,
    BListGroup,
    BListGroupItem,
    BTableSimple,
    BThead,
    BTr,
    BTh,
    BTd,
    BTbody,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      statusTabs: STATUS_TABS,
      status: "pending",
      requests: [],
      counts: { pending: 0, approved: 0, rejected: 0, all: 0 },
      loading: false,
      searchQuery: "",
      showDetails: false,
      selected: null,
      detailsLoading: false,
      openingDocument: null,
      isAddNewSchoolSidebarActive: false,
      prefill: null,
    };
  },
  computed: {
    currentTab() {
      return this.statusTabs.find((t) => t.value === this.status) || this.statusTabs[0];
    },
    filteredRequests() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.requests;
      return this.requests.filter((r) =>
        [r.schoolName, r.contactName, r.contactPhone, r.contactEmail, r.city]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
    schoolDetails() {
      const r = this.selected || {};
      return [
        { label: "Address", value: r.address || "-" },
        { label: "City", value: r.city || "-" },
        { label: "Students", value: this.formatNumber(r.totalStudents) },
        { label: "Staff users needed", value: this.formatNumber(r.staffCount) },
        { label: "Premium seats", value: this.formatNumber(r.premiumSeats || 0) },
        { label: "Package", value: r.packageName || "-" },
      ];
    },
  },
  created() {
    this.getRequests();
  },
  methods: {
    authHeaders() {
      return {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("_t"),
        },
      };
    },
    getRequests() {
      this.loading = true;
      const query = this.status ? `&status=${this.status}` : "";
      axios
        .get(process.env.VUE_APP_API_URL + `/school/requests?source=portal${query}`, this.authHeaders())
        .then((response) => {
          this.requests = response.data.requests || [];
          this.counts = { ...this.counts, ...(response.data.counts || {}) };
        })
        .catch((error) => {
          this.toast(this.errorMessage(error, "Could not load school sign-ups"), "danger");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeStatus(status) {
      if (this.status === status) return;
      this.status = status;
      this.getRequests();
    },
    viewRequest(request) {
      this.selected = { ...request };
      this.showDetails = true;
      this.detailsLoading = true;
      axios
        .get(process.env.VUE_APP_API_URL + `/school/requests/detail?request_id=${request._id}`, this.authHeaders())
        .then((response) => {
          if (this.selected && this.selected._id === request._id) {
            this.selected = response.data.request;
          }
        })
        .catch((error) => {
          this.toast(this.errorMessage(error, "Could not load the request"), "danger");
        })
        .finally(() => {
          this.detailsLoading = false;
        });
    },
    closeDetails() {
      this.selected = null;
      this.openingDocument = null;
    },
    documentId(doc) {
      return typeof doc._id === "object" && doc._id ? doc._id.$oid : doc._id;
    },
    openDocument(doc) {
      const id = this.documentId(doc);
      // Opened before the download so the browser doesn't block the new tab
      const tab = window.open("", "_blank");
      this.openingDocument = id;
      axios
        .get(process.env.VUE_APP_API_URL + `/school/requests/document?request_id=${this.selected._id}&document_id=${id}`, {
          ...this.authHeaders(),
          responseType: "blob",
        })
        .then((response) => {
          const url = URL.createObjectURL(new Blob([response.data], { type: doc.type }));
          if (tab) tab.location.href = url;
          else window.location.href = url;
          setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
        })
        .catch(() => {
          if (tab) tab.close();
          this.toast("Could not open the document", "danger");
        })
        .finally(() => {
          this.openingDocument = null;
        });
    },
    isPending(request) {
      return !request.status || request.status === "pending";
    },
    statusVariant(status) {
      if (status === "approved") return "success";
      if (status === "rejected") return "danger";
      return "warning";
    },
    approveRequest(request) {
      this.prefill = {
        request_id: request._id,
        school_name: request.schoolName || "",
        staff_count: request.staffCount || null,
        premium_seats: request.premiumSeats || 0,
        package_id: request.packageId || null,
        package_name: request.packageName || null,
        school_img: request.schoolImg || null,
        admin_email: request.contactEmail || null,
      };
      this.showDetails = false;
      this.isAddNewSchoolSidebarActive = true;
    },
    createSchoolFromRequest(schoolData) {
      axios
        .post(process.env.VUE_APP_API_URL + "/school/create", schoolData, this.authHeaders())
        .then((response) => {
          if (response.data.message == "School created successfully") {
            this.toast(`"${schoolData.school_name}" approved. The school admin can now log in to the school portal.`, "success");
          } else {
            this.toast(response.data.message || "School not added", "danger");
          }
          this.prefill = null;
          this.getRequests();
        })
        .catch((error) => {
          this.toast(this.errorMessage(error, "School not added"), "danger");
          this.getRequests();
        });
    },
    rejectRequest(request) {
      Swal.fire({
        title: "Reject sign-up?",
        html: `Reject the sign-up of <strong>${this.escapeHtml(request.schoolName || "this school")}</strong>. The school sees the reason when it tries to log in.`,
        icon: "warning",
        input: "textarea",
        inputPlaceholder: "Reason for rejecting...",
        inputAttributes: { maxlength: 1000 },
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#ea5455",
        cancelButtonColor: "#82868b",
        showLoaderOnConfirm: true,
        inputValidator: (value) => {
          if (!value || !value.trim()) return "Please enter a reason";
          return undefined;
        },
        preConfirm: (reason) =>
          axios
            .post(
              process.env.VUE_APP_API_URL + "/school/requests/reject",
              { request_id: request._id, reason: reason.trim() },
              this.authHeaders()
            )
            .then((response) => response.data)
            .catch((error) => {
              Swal.showValidationMessage(this.errorMessage(error, "Could not reject the request"));
            }),
        allowOutsideClick: () => !Swal.isLoading(),
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          this.toast("School sign-up rejected", "success");
          this.showDetails = false;
          this.getRequests();
        }
      });
    },
    sendPropsToSchoolGrade(schoolName, schoolId) {
      localStorage.setItem("schoolName", JSON.stringify(schoolName));
      localStorage.setItem("schoolid", JSON.stringify(schoolId));
    },
    formatDate(date) {
      if (!date) return "-";
      const d = moment(date);
      return d.isValid() ? d.format("DD MMM YYYY, hh:mm A") : "-";
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "-";
      return Number(value).toLocaleString();
    },
    formatSize(bytes) {
      if (!bytes) return "-";
      return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
    },
    fileType(type) {
      return FILE_TYPES[type] || type || "File";
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
    },
    escapeHtml(text) {
      const div = document.createElement("div");
      div.innerText = text;
      return div.innerHTML;
    },
    errorMessage(error, fallback) {
      const data = error && error.response && error.response.data;
      if (data) {
        if (data.errors) {
          const first = Object.values(data.errors)[0];
          if (Array.isArray(first) && first.length) return first[0];
        }
        if (data.message) return data.message;
        if (data.error) return data.error;
      }
      return fallback;
    },
    toast(text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Notification",
          icon: "BellIcon",
          text,
          variant,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.status-filter-card {
  border: 1px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 18px rgba(34, 41, 47, 0.12);
  }

  &--active {
    border-width: 1px;
    border-style: solid;
  }
}

.request-note-full {
  white-space: pre-line;
}

.school-requests-table td {
  vertical-align: middle;
}
</style>
